import { Component as ReactComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { SearchIcon as Search } from '@/components/icons/lucide-search'
import { Home, AlertTriangle } from 'lucide-react'
import i18n from '@/i18n';
import { isChunkLoadFailure, reloadForStaleChunk } from '@/lib/chunk-load-recovery';

interface ErrorPageProps {
  type?: 'not-found' | 'server-error' | 'unauthorized' | 'generic';
  title?: string;
  message?: string;
  onRetry?: () => void;
}

export const ErrorPage = ({
  type = 'generic',
  title,
  message,
  onRetry,
}: ErrorPageProps) => {
  const { t } = useTranslation();

  const content = {
    'not-found': {
      title: title || t('error.notFound.title'),
      message: message || t('error.notFound.message'),
      icon: Search,
    },
    'server-error': {
      title: title || t('error.serverError.title'),
      message: message || t('error.serverError.message'),
      icon: AlertTriangle,
    },
    unauthorized: {
      title: title || t('error.unauthorized.title'),
      message: message || t('error.unauthorized.message'),
      icon: AlertTriangle,
    },
    generic: {
      title: title || t('error.title'),
      message: message || t('error.message'),
      icon: AlertTriangle,
    },
  };

  const { title: pageTitle, message: pageMessage, icon: Icon } = content[type];

  return (
    <div className="flex min-h-[100vh] flex-col items-center justify-center px-4 text-center">
      <div className="mb-6 rounded-full bg-destructive/10 p-4">
        <Icon className="h-12 w-12 text-destructive" aria-hidden="true" />
      </div>
      <h1 className="mb-2 text-3xl font-bold tracking-tight">{pageTitle}</h1>
      <p className="mb-8 max-w-md text-muted-foreground">{pageMessage}</p>
      <div className="flex gap-4">
        <Button asChild>
          <Link to="/">
            <Home className="mr-2 h-4 w-4" aria-hidden="true" />
            {t('error.home')}
          </Link>
        </Button>
        {type === 'not-found' && (
          <Button asChild variant="outline">
            <Link to="/explore">{t('error.notFound.explore')}</Link>
          </Button>
        )}
        {onRetry && (
          <Button variant="outline" onClick={onRetry}>
            {t('common.retry')}
          </Button>
        )}
      </div>
    </div>
  );
};

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends ReactComponent<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      const err = this.state.error
      const chunkStale = isChunkLoadFailure(err)
      return (
        <ErrorPage
          type="generic"
          title={chunkStale ? i18n.t('error.chunkOutdated.title') : undefined}
          message={chunkStale ? i18n.t('error.chunkOutdated.message') : undefined}
          onRetry={() => {
            if (chunkStale) {
              reloadForStaleChunk({ force: true })
              return
            }
            this.setState({ hasError: false, error: null })
          }}
        />
      )
    }

    return this.props.children;
  }
}